import { CaretDown, Check } from "@phosphor-icons/react";
import { Popover, Text } from "@radix-ui/themes";
import cx from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Which review engine the user picked. Sent with the next message (`reviewMode`)
 * and consulted server-side only when v2 production routing is enabled:
 * "standard" runs the legacy v1 engine, "deep" runs v2.
 */
export type ReviewMode = "standard" | "deep";

type ModeOption = {
  value: ReviewMode;
  labelKey: string;
  badgeKey?: string;
  copyKey: string;
};

// House copy: describe review THOROUGHNESS + rough time, never the internal
// engine names (v1/v2) or pipeline stages — users don't know those.
const MODE_OPTIONS: readonly ModeOption[] = [
  {
    value: "standard",
    labelKey: "reviewMode.standard.label",
    badgeKey: "reviewMode.standard.badge",
    copyKey: "reviewMode.standard.copy",
  },
  {
    value: "deep",
    labelKey: "reviewMode.deep.label",
    copyKey: "reviewMode.deep.copy",
  },
];

const TRIGGER_LABEL_KEY: Record<ReviewMode, string> = {
  standard: "reviewMode.standard.label",
  deep: "reviewMode.deep.label",
};

/**
 * Artwork review-mode picker for the Ask Puntt composer — a dropdown (Radix
 * Themes `Popover`) whose trigger shows the current mode and whose menu offers
 * Standard vs Deep Review with plain-language descriptions. Built entirely from
 * the project UI-kit (`Popover`, `Text`, phosphor icons). a11y: trigger is a
 * labelled button; options are `role="menuitemradio"` with `aria-checked`.
 * The choice rides on the next message as `reviewMode`: Standard runs the v1
 * review engine, Deep runs v2.
 */
export function ReviewModeControl({
  mode,
  onModeChange,
  disabled = false,
}: {
  mode: ReviewMode;
  onModeChange: (mode: ReviewMode) => void;
  /** Locks the picker (no menu, muted) — e.g. once a review is already running. */
  disabled?: boolean;
}) {
  const { t } = useTranslation("ui");
  const [open, setOpen] = useState(false);
  const isOpen = open && !disabled;

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(next) => {
        if (!disabled) setOpen(next);
      }}
    >
      <Popover.Trigger>
        <button
          type="button"
          data-testid="ask-puntt-review-mode"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-label={t("reviewMode.triggerAriaLabel", {
            mode: t(TRIGGER_LABEL_KEY[mode]),
          })}
          disabled={disabled}
          title={disabled ? t("reviewMode.disabledTitle") : undefined}
          className={cx(
            // `bg-transparent` is load-bearing: Tailwind preflight is off
            // (tailwind.config.js), so a bare <button> keeps the UA's
            // `buttonface` grey. Radix Themes' own buttons escape this via
            // `rt-reset`; Popover.Trigger only slots props onto this one.
            "flex items-center gap-1.5 rounded-xl bg-transparent p-1.5 text-sm font-medium transition-colors",
            disabled
              ? "cursor-not-allowed text-puntt-neutral-gray-9"
              : "text-puntt-neutral-gray-12 hover:text-puntt-neutral-gray-11",
          )}
        >
          {t(TRIGGER_LABEL_KEY[mode])}
          <CaretDown
            size={12}
            weight="bold"
            aria-hidden="true"
            className={cx(
              "text-puntt-neutral-gray-9 transition-transform duration-150",
              isOpen && "rotate-180",
            )}
          />
        </button>
      </Popover.Trigger>
      <Popover.Content
        role="menu"
        aria-label={t("reviewMode.menuAriaLabel")}
        data-testid="ask-puntt-review-mode-menu"
        align="end"
        side="top"
        sideOffset={8}
        className="max-h-[min(70vh,480px)] w-[min(365px,calc(100vw-32px))] overflow-y-auto"
      >
        <Text
          as="p"
          size="2"
          weight="bold"
          className="px-1 pb-1 text-puntt-neutral-gray-12"
        >
          {t("reviewMode.heading")}
        </Text>
        <Text as="p" size="1" className="px-1 pb-2 text-puntt-neutral-gray-11">
          {t("reviewMode.description")}
        </Text>
        <div className="flex flex-col gap-2">
          {MODE_OPTIONS.map((opt) => {
            const selected = opt.value === mode;
            return (
              <button
                key={opt.value}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                data-testid={`ask-puntt-review-mode-${opt.value}`}
                aria-label={t("reviewMode.optionAriaLabel", {
                  mode: t(opt.labelKey),
                })}
                onClick={() => {
                  onModeChange(opt.value);
                  setOpen(false);
                }}
                className={cx(
                  "grid grid-cols-[1fr_auto] items-start gap-2 rounded-xl border p-3 text-left transition-colors",
                  selected
                    ? "border-puntt-blue-8 bg-puntt-blue-2"
                    : "bg-puntt-neutral-white border-puntt-neutral-gray-6 hover:bg-puntt-neutral-gray-2",
                )}
              >
                <span className="flex flex-col gap-1">
                  <span className="flex items-center gap-2">
                    <Text
                      size="2"
                      weight="medium"
                      className="text-puntt-neutral-gray-12"
                    >
                      {t(opt.labelKey)}
                    </Text>
                    {opt.badgeKey && (
                      <Text
                        size="1"
                        className="rounded bg-puntt-neutral-gray-4 px-1.5 py-0.5 text-puntt-neutral-gray-11"
                      >
                        {t(opt.badgeKey)}
                      </Text>
                    )}
                  </span>
                  <Text size="1" className="text-puntt-neutral-gray-11">
                    {t(opt.copyKey)}
                  </Text>
                </span>
                {selected && (
                  <Check
                    size={16}
                    weight="bold"
                    aria-hidden="true"
                    className="mt-0.5 text-puntt-blue-9"
                  />
                )}
              </button>
            );
          })}
        </div>
        <Text as="p" size="1" className="px-1 pt-2 text-puntt-neutral-gray-10">
          {t("reviewMode.scopeNote")}
        </Text>
      </Popover.Content>
    </Popover.Root>
  );
}
